import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";

import type { TrackPoint, TrackingSport } from "@/lib/tracking/types";

export const NATIVE_TRACKING_EVENT = "trackingPoint";
export const NATIVE_TRACKING_ERROR_EVENT = "trackingError";

export type NativeTrackingPlatform = "ios" | "android" | "web";
export type NativeTrackingPermissionState =
  | "granted"
  | "prompt"
  | "prompt-with-rationale"
  | "denied"
  | "restricted"
  | "unavailable";

export type NativeTrackingMode = "web" | "screen-on-native" | "background-native";

export interface NativeTrackingStatus {
  available: boolean;
  platform: NativeTrackingPlatform;
  foreground: NativeTrackingPermissionState;
  background: NativeTrackingPermissionState;
  precise: boolean;
  active: boolean;
  scripted: boolean;
  recoveryAvailable?: boolean;
  paused?: boolean;
}

export interface NativeTrackingStartOptions {
  sessionId?: string;
  startedAt?: number;
  sport: TrackingSport;
  intervalMs: number;
  distanceFilterM: number;
  background: boolean;
}

export interface NativeTrackingConfigureOptions {
  intervalMs: number;
  distanceFilterM: number;
}

export interface NativeScriptedFeedOptions {
  sessionId?: string;
  startedAt?: number;
  sport: TrackingSport;
  intervalMs: number;
}

export interface NativeTrackPointPayload {
  timestamp: number;
  latitude: number;
  longitude: number;
  altitude: number | null;
  horizontalAccuracy: number | null;
  speed: number | null;
}

export interface NativeTrackingErrorPayload {
  code: string;
  message: string;
}

export interface PeakWaveTrackingPlugin {
  getStatus(): Promise<NativeTrackingStatus>;
  requestForegroundPermission(): Promise<NativeTrackingStatus>;
  requestBackgroundPermission(): Promise<NativeTrackingStatus>;
  start(options: NativeTrackingStartOptions): Promise<NativeTrackingStatus>;
  stop(): Promise<NativeTrackingStatus>;
  configure(options: NativeTrackingConfigureOptions): Promise<NativeTrackingStatus>;
  startScriptedFeed(options: NativeScriptedFeedOptions): Promise<NativeTrackingStatus>;
  readRecording(options: { cursor: number }): Promise<unknown>;
  clearRecording(options: { sessionId: string }): Promise<void>;
  setPaused(options: { paused: boolean }): Promise<NativeTrackingStatus>;
  addListener(
    eventName: typeof NATIVE_TRACKING_EVENT,
    listenerFunc: (event: NativeTrackPointPayload) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: typeof NATIVE_TRACKING_ERROR_EVENT,
    listenerFunc: (event: NativeTrackingErrorPayload) => void,
  ): Promise<PluginListenerHandle>;
}

export const PeakWaveTracking = registerPlugin<PeakWaveTrackingPlugin>("PeakWaveTracking");

export const NATIVE_TRACKING_COPY = {
  foreground:
    "Allow location while the app is open so PeakWave can record your session after you press Start.",
  background:
    "Allow background location only if you want recording to continue when the screen is off. PeakWave records only while a session is running, shows a native recording indicator or notification, and uses more battery. Stop tracking to stop background location.",
} as const;

const DEFAULT_STATUS: NativeTrackingStatus = {
  available: false,
  platform: "web",
  foreground: "unavailable",
  background: "unavailable",
  precise: false,
  active: false,
  scripted: false,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function nullableFiniteNumber(value: unknown): number | null {
  return value == null ? null : finiteNumber(value);
}

export function normaliseNativeTrackPoint(raw: unknown): TrackPoint | null {
  if (!isRecord(raw)) return null;

  const t = finiteNumber(raw.timestamp);
  const lat = finiteNumber(raw.latitude);
  const lng = finiteNumber(raw.longitude);
  if (t == null || lat == null || lng == null) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;

  return {
    t,
    lat,
    lng,
    ele: nullableFiniteNumber(raw.altitude),
    accuracy: nullableFiniteNumber(raw.horizontalAccuracy),
    speedMs: nullableFiniteNumber(raw.speed),
  };
}

export function nativePayloadFromTrackPoint(point: TrackPoint): NativeTrackPointPayload {
  return {
    timestamp: point.t,
    latitude: point.lat,
    longitude: point.lng,
    altitude: point.ele,
    horizontalAccuracy: point.accuracy,
    speed: point.speedMs ?? null,
  };
}

export function resolveNativeTrackingMode(status: NativeTrackingStatus): NativeTrackingMode {
  if (!status.available || status.foreground !== "granted") return "web";
  return status.background === "granted" ? "background-native" : "screen-on-native";
}

export function nativeTrackingStartOptions(
  sport: TrackingSport,
  intervalMs: number,
  background: boolean,
): NativeTrackingStartOptions {
  return {
    sport,
    intervalMs,
    distanceFilterM: intervalMs >= 5000 ? 10 : intervalMs >= 3000 ? 6 : 3,
    background,
  };
}

export function getNativeTrackingPlugin(): PeakWaveTrackingPlugin | null {
  if (typeof window === "undefined") return null;
  if (!Capacitor.isNativePlatform()) return null;
  return PeakWaveTracking;
}

export async function getNativeTrackingStatus(): Promise<NativeTrackingStatus> {
  const plugin = getNativeTrackingPlugin();
  if (!plugin) return DEFAULT_STATUS;

  try {
    return await plugin.getStatus();
  } catch {
    return DEFAULT_STATUS;
  }
}
